import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";
import Container from "../global/container";
import { MagicCard } from "../ui/magic-card";
import { cn } from "@/lib";

interface Stat {
  value: string;
  label: string;
  icon?: LucideIcon;
  description?: string;
  trend?: "up" | "down" | "neutral";
  color?: string;
}

interface StatsGridProps {
  stats: Stat[];
  title?: string;
  subtitle?: string;
  variant?: "cards" | "simple" | "gradient";
  columns?: 2 | 3 | 4;
  className?: string;
  animated?: boolean;
}

const StatsGrid = ({
  stats,
  title,
  subtitle,
  variant = "cards",
  columns = 3,
  className,
  animated = true,
}: StatsGridProps) => {
  const getGridClasses = () => {
    switch (columns) {
      case 2:
        return "grid-cols-1 md:grid-cols-2";
      case 4:
        return "grid-cols-2 md:grid-cols-4";
      default:
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    }
  };

  const getContainerClasses = () => {
    switch (variant) {
      case "gradient":
        return "p-8 rounded-2xl bg-gradient-to-r from-blue-50/50 to-purple-50/50 border border-border/50";
      case "simple":
        return "";
      default:
        return "p-8 rounded-2xl bg-gradient-to-r from-blue-50/50 to-purple-50/50 border border-border/50";
    }
  };

  const StatCard = ({ stat, index }: { stat: Stat; index: number }) => {
    const StatIcon = stat.icon;

    if (variant === "simple") {
      return (
        <motion.div
          className="text-center group"
          initial={animated ? { opacity: 0, scale: 0.8 } : {}}
          whileInView={animated ? { opacity: 1, scale: 1 } : {}}
          transition={animated ? { duration: 0.5, delay: index * 0.1 } : {}}
          viewport={{ once: true }}
          whileHover={animated ? { scale: 1.05 } : {}}
          data-oid="d9rsi5."
        >
          {StatIcon && (
            <motion.div
              className="mb-4 flex justify-center"
              whileHover={animated ? { scale: 1.1 } : {}}
              transition={{ duration: 0.2 }}
              data-oid=".pc5hjh"
            >
              <StatIcon
                className={cn("h-8 w-8", stat.color || "text-blue-600")}
                data-oid="u-q_k-e"
              />
            </motion.div>
          )}
          <motion.div
            className={cn(
              "text-3xl md:text-4xl font-bold mb-2",
              stat.color || "text-blue-600",
            )}
            whileHover={animated ? { scale: 1.1 } : {}}
            transition={{ duration: 0.2 }}
            data-oid="lc8x0-n"
          >
            {stat.value}
          </motion.div>
          <div
            className="text-sm text-muted-foreground group-hover:text-foreground transition-colors"
            data-oid="yoht4uk"
          >
            {stat.label}
          </div>
          {stat.description && (
            <p
              className="text-xs text-muted-foreground mt-1"
              data-oid="utc6s6c"
            >
              {stat.description}
            </p>
          )}
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={animated ? { opacity: 0, y: 20 } : {}}
        whileInView={animated ? { opacity: 1, y: 0 } : {}}
        transition={animated ? { duration: 0.5, delay: index * 0.1 } : {}}
        viewport={{ once: true }}
        className="h-full"
        data-oid="gxl_f1j"
      >
        <MagicCard
          gradientFrom="#3b82f6"
          gradientTo="#8b5cf6"
          gradientColor="rgba(59,130,246,0.05)"
          className="text-center p-6 rounded-2xl border bg-card/50 backdrop-blur-sm h-full group hover:scale-105 transition-transform duration-300"
          data-oid="rq_q7t9"
        >
          {StatIcon && (
            <motion.div
              className="mb-4 flex justify-center"
              whileHover={animated ? { scale: 1.1, rotate: 5 } : {}}
              transition={{ duration: 0.2 }}
              data-oid="r-d677y"
            >
              <StatIcon
                className={cn("h-8 w-8", stat.color || "text-blue-600")}
                data-oid="rn0zn5g"
              />
            </motion.div>
          )}
          <motion.div
            className={cn(
              "text-3xl md:text-4xl font-bold mb-2",
              stat.color || "text-blue-600",
            )}
            whileHover={animated ? { scale: 1.1 } : {}}
            transition={{ duration: 0.2 }}
            data-oid="f45-42a"
          >
            {stat.value}
          </motion.div>
          <div
            className="text-sm text-muted-foreground group-hover:text-foreground transition-colors"
            data-oid="nn5jk2k"
          >
            {stat.label}
          </div>
          {stat.description && (
            <p
              className="text-xs text-muted-foreground mt-2 group-hover:text-muted-foreground/80 transition-colors"
              data-oid="t:gdppa"
            >
              {stat.description}
            </p>
          )}
          {stat.trend && (
            <div
              className={cn(
                "text-xs font-medium mt-2",
                stat.trend === "up" && "text-green-600",
                stat.trend === "down" && "text-red-600",
                stat.trend === "neutral" && "text-muted-foreground",
              )}
              data-oid="4qwb32n"
            >
              {stat.trend === "up" && "↗ Growing"}
              {stat.trend === "down" && "↘ Declining"}
              {stat.trend === "neutral" && "→ Stable"}
            </div>
          )}
        </MagicCard>
      </motion.div>
    );
  };

  return (
    <Container className={className} data-oid="j5fzfy8">
      {(title || subtitle) && (
        <motion.div
          className="text-center mb-12"
          initial={animated ? { opacity: 0, y: 20 } : {}}
          whileInView={animated ? { opacity: 1, y: 0 } : {}}
          transition={animated ? { duration: 0.6 } : {}}
          viewport={{ once: true }}
          data-oid="-z2xmb6"
        >
          {title && (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              data-oid=".-zt52j"
            >
              {title}
            </h2>
          )}
          {subtitle && (
            <p
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
              data-oid="dh-3-kn"
            >
              {subtitle}
            </p>
          )}
        </motion.div>
      )}

      <motion.div
        className={cn("grid gap-6", getGridClasses(), getContainerClasses())}
        initial={animated ? { opacity: 0, y: 20 } : {}}
        whileInView={animated ? { opacity: 1, y: 0 } : {}}
        transition={animated ? { duration: 0.6, delay: 0.2 } : {}}
        viewport={{ once: true }}
        data-oid="vdc-a9m"
      >
        {stats.map((stat, index) => (
          <StatCard key={index} stat={stat} index={index} data-oid="9t56h0j" />
        ))}
      </motion.div>
    </Container>
  );
};

export default StatsGrid;
